import type { CurrentUser } from '@zhiyue/core';
import {
  listOpenPlatformMembers,
  type OpenPlatformMember,
} from '@/services/bff/open-platform.api';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';

type MemberDirectoryUser = Pick<CurrentUser, 'tenantUuid' | 'memberUuid'> & {
  organizationUuid?: string;
};

let identityKey = '';
let loading: Promise<void> | null = null;
const members = new Map<string, OpenPlatformMember>();

const getIdentityKey = (user?: Partial<MemberDirectoryUser> | null) =>
  [user?.tenantUuid, user?.organizationUuid, user?.memberUuid]
    .map((value) => String(value || '').trim())
    .join('|');

export const clearMemberDirectory = () => {
  identityKey = '';
  loading = null;
  members.clear();
};

export const loadMemberDirectory = async (
  user?: Partial<MemberDirectoryUser> | null,
): Promise<void> => {
  const nextIdentityKey = getIdentityKey(user);
  const memberUuid = String(user?.memberUuid || '').trim();
  if (!nextIdentityKey || !memberUuid) return clearMemberDirectory();
  if (identityKey === nextIdentityKey && (loading || members.size)) {
    return loading || undefined;
  }

  identityKey = nextIdentityKey;
  members.clear();
  loading = fetchOpenPlatformToken(user as MemberDirectoryUser)
    .then(({ accessToken }) => listOpenPlatformMembers(accessToken, memberUuid))
    .then((items) => {
      if (identityKey !== nextIdentityKey) return;
      items.forEach((member) => {
        members.set(member.memberUuid, member);
      });
    })
    .finally(() => {
      if (identityKey === nextIdentityKey) loading = null;
    });
  return loading;
};

export const getMember = (memberUuid?: string | null) =>
  memberUuid ? members.get(memberUuid) : undefined;

export const getMemberName = (memberUuid?: string | null): string =>
  getMember(memberUuid)?.name || '-';

export const getMembers = (): OpenPlatformMember[] =>
  Array.from(members.values());
